import { Helmet } from "react-helmet-async";
import { ORG_REF, PERSON_REF, WEBSITE_REF } from "@/data/entity";

interface StructuredDataProps {
  type: 'service' | 'organization' | 'faq' | 'howto' | 'article' | 'collection' | 'profile' | 'contact';
  data: any;
}

export const StructuredData = ({ type, data }: StructuredDataProps) => {
  const generateSchema = () => {
    const baseContext = "https://schema.org";
    
    switch (type) {
      // Fase 4: no se crea una segunda Organization. La página "sobre mí" es
      // una ProfilePage cuya entidad principal es la Person global.
      case 'organization':
      case 'profile':
        return {
          "@context": baseContext,
          "@type": "ProfilePage",
          "@id": data.url ? `${data.url}#profilepage` : undefined,
          "name": data.name,
          "description": data.description,
          "url": data.url,
          "isPartOf": WEBSITE_REF,
          "mainEntity": PERSON_REF,
          "about": PERSON_REF,
          "publisher": ORG_REF,
        };

      case 'contact':
        return {
          "@context": baseContext,
          "@type": "ContactPage",
          "@id": data.url ? `${data.url}#contactpage` : undefined,
          "name": data.name,
          "description": data.description,
          "url": data.url,
          "isPartOf": WEBSITE_REF,
          "about": ORG_REF,
          "publisher": ORG_REF,
        };

      case 'collection':
        return {
          "@context": baseContext,
          "@type": "CollectionPage",
          "name": data.name,
          "description": data.description,
          "url": data.url,
          "inLanguage": data.inLanguage,
          "isPartOf": WEBSITE_REF,
          "publisher": ORG_REF,
          "hasPart": data.hasPart?.map((part: any) => ({
            "@type": "Article",
            "headline": part.headline,
            "url": part.url
          }))
        };

      case 'service':
        return {
          "@context": baseContext,
          "@type": "Service",
          ...(data.url ? { "@id": `${data.url}#service` } : {}),
          "name": data.name,
          "description": data.description,
          "url": data.url,
          "provider": ORG_REF,
          "serviceType": data.serviceType,
          ...(data.areaServed ? { areaServed: data.areaServed } : {}),
          ...(data.offerings?.length
            ? {
                hasOfferCatalog: {
                  "@type": "OfferCatalog",
                  "name": data.name,
                  "itemListElement": data.offerings.map((offer: any, index: number) => ({
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Service",
                      "name": offer.name,
                      "description": offer.description
                    },
                    "position": index + 1
                  }))
                }
              }
            : {}),
          ...(data.targetAudience
            ? { audience: { "@type": "Audience", "audienceType": data.targetAudience } }
            : {})
        };


      case 'faq':
        return {
          "@context": baseContext,
          "@type": "FAQPage",
          "mainEntity": data.questions.map((item: any) => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        };

      case 'howto':
        return {
          "@context": baseContext,
          "@type": "HowTo",
          "name": data.name,
          "description": data.description,
          "image": data.image,
          "totalTime": data.duration,
          "estimatedCost": data.cost,
          "step": (data.steps ?? []).map((step: any, index: number) => ({
            "@type": "HowToStep",
            "position": index + 1,
            "name": step.name,
            "text": step.description,
            "url": step.url
          }))
        };

      case 'article':
        return {
          "@context": baseContext,
          "@type": data["@type"] === "BlogPosting" ? "BlogPosting" : "Article",
          "headline": data.headline ?? data.title,
          "description": data.description,
          "inLanguage": data.inLanguage,
          // Autoría única: siempre la Person global, nunca una copia.
          "author": PERSON_REF,
          "publisher": ORG_REF,
          "datePublished": data.datePublished,
          // dateModified solo si el contenido cambió realmente.
          ...(data.dateModified && data.dateModified !== data.datePublished
            ? { dateModified: data.dateModified }
            : {}),
          "mainEntityOfPage": data.mainEntityOfPage ?? data.url,
          "isPartOf": WEBSITE_REF,
          ...(data.articleSection ? { articleSection: data.articleSection } : {}),
          "keywords": data.keywords,
          "about": data.topics?.map((topic: string) => ({
            "@type": "Thing",
            "name": topic
          }))
        };



      default:
        return null;
    }
  };

  const schema = generateSchema();

  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// LLM-specific optimization components
export const LLMOptimizedContent = ({ children, summary, keyPoints }: {
  children: React.ReactNode;
  summary?: string;
  keyPoints?: string[];
}) => (
  <div className="llm-optimized-content">
    {summary && (
      <div className="bg-primary/5 border-l-4 border-primary p-4 mb-6">
        <h3 className="font-semibold text-primary mb-2">TL;DR</h3>
        <p className="text-sm text-muted-foreground">{summary}</p>
      </div>
    )}
    
    {keyPoints && keyPoints.length > 0 && (
      <div className="bg-muted/30 rounded-lg p-4 mb-6">
        <h3 className="font-semibold mb-3">Key Points:</h3>
        <ul className="space-y-2">
          {keyPoints.map((point, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span className="text-sm">{point}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
    
    {children}
  </div>
);

export const FAQSection = ({ questions, language = 'es' }: {
  questions: Array<{ question: string; answer: string; }>;
  language?: 'es' | 'en';
}) => {
  const titles = {
    es: "Preguntas Frecuentes",
    en: "Frequently Asked Questions"
  };

  return (
    <>
      <StructuredData type="faq" data={{ questions }} />
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-center mb-8 heading-gradient">
            {titles[language]}
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {questions.map((item, index) => (
              <div key={index} className="bg-background rounded-lg p-6 shadow-sm border">
                <h3 className="font-semibold text-lg mb-3 text-primary">
                  Q: {item.question}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  <span className="font-medium text-foreground">A:</span> {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};